import React, { useMemo } from 'react';

import { AssetClass, PositionType, ResourceType } from '@/cip/_openapi/portfolio-optimizer';
import { usePositions } from '@/cip/api/position';
import { positionListColumns } from '@/cip/Position/positionListColumns';
import { AssetClassBadge } from '@/components/AssetClassBadge';
import { ServerDataTable } from '@/components/table/DataTable';
import { TradeSideBadge } from '@/components/TradeSideBadge';
import { useRequiredParams } from 'utils';

import { AccountNavCash } from './AccountNavCash';
import {
    accountTableClassName,
    AccountTableLayout,
    DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
} from './accountTableDefaults';
import { AccountTableToolbar } from './AccountTableToolbar';
import { LargerFlagCell } from './LargerFlagCell';
import { HoldingsViewToggle } from './new-account/AccountHoldingsTab';
import { HOLDINGS_POSITION_COLUMNS, holdingsColumns, holdingsColumnVisibility } from './new-account/holdings-columns';
import { isPnlMoneyColumn, isPnlPercentColumn, PnlMoneyCell, PnlPercentCell } from './pnlCellRenderers';
import { useAccountTableState } from './useAccountTableState';

// visible: symbol, description, assetClass, quantity, closePrice, costBasis, grossExposure, pnlUnrealized, returnUnrealized, accruedInterest
const POSITIONS_DEFAULT_COLUMN_VISIBILITY = {
    ...DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
    assetType: false,
    side: false,
    netExposure: false,
    pnlRealized: false,
    returnRealized: false,
    term: false,
    primaryExchange: false,
    cusip: false,
    isin: false,
    subAssetType: false,
    allocationType: false,
    investmentSleeve: false,
    maturityDate: false,
    gicsCode: false,
    gicsSector: false,
    gicsIndustry: false,
};

const EXCLUDE_POSITION_COLUMNS = ['pId', 'portfolioGuid', 'portfolioName', 'householdName', 'aC', 'referencePortfolio'];

const HOLDINGS_POSITION_VISIBILITY = holdingsColumnVisibility(positionListColumns, HOLDINGS_POSITION_COLUMNS);

export const AccountPositionsTab = ({ layout = 'legacy' }: { layout?: AccountTableLayout }) => {
    const holdings = layout === 'new';
    const { accountGUID } = useRequiredParams(['accountGUID']);

    const {
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        resetSettings,
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        accountFilter,
        allFilters,
    } = useAccountTableState({
        tableId: holdings ? 'accountHoldingsPositions' : 'accountPositions',
        accountGUID,
        excludeColumns: EXCLUDE_POSITION_COLUMNS,
        defaultColumnVisibility: holdings ? HOLDINGS_POSITION_VISIBILITY : POSITIONS_DEFAULT_COLUMN_VISIBILITY,
        defaultColumnOrder: holdings ? HOLDINGS_POSITION_COLUMNS : DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
        defaultPageSize: 1000,
    });

    const {
        data: positionsPage,
        isLoading: isPositionsLoading,
        isFetching: isPositionsFetching,
    } = usePositions({
        columnFilters: allFilters,
        pagination: pagination,
        sorting: sorting,
        dataVariant: 'report',
    });

    const positionsColumnsWithCustomRenderers = useMemo(
        () =>
            (holdings ? holdingsColumns(positionListColumns) : positionListColumns).map((col) => {
                if (col.id === 'country') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => string }) => <LargerFlagCell alpha3={getValue()} />,
                    };
                }
                if (col.id === 'assetClass') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => AssetClass }) => (
                            <AssetClassBadge assetClass={getValue()} />
                        ),
                    };
                }
                if (col.id === 'side') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => string }) => <TradeSideBadge side={getValue()} />,
                    };
                }
                if (isPnlMoneyColumn(col.id)) {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => number | undefined }) => (
                            <PnlMoneyCell value={getValue()} />
                        ),
                    };
                }
                if (isPnlPercentColumn(col.id)) {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => number | undefined }) => (
                            <PnlPercentCell value={getValue()} />
                        ),
                    };
                }
                return col;
            }),
        [holdings],
    );

    return (
        <ServerDataTable
            data={positionsPage?.positions || []}
            columns={positionsColumnsWithCustomRenderers}
            state={{ pagination, sorting, columnFilters, columnVisibility, columnOrder, columnSizing }}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            onColumnFiltersChange={setColumnFilters}
            onColumnVisibilityChange={setColumnVisibility}
            onColumnOrderChange={setColumnOrder}
            onColumnSizingChange={setColumnSizing}
            isLoading={isPositionsLoading}
            isFetching={isPositionsFetching}
            emptyMessage="No positions found"
            className={accountTableClassName(layout)}
            rowCount={positionsPage?.totalNr ?? 0}
            deferInitialRender={true}
            renderToolbar={(table) => (
                <AccountTableToolbar
                    table={table}
                    layout={layout}
                    extraControls={<HoldingsViewToggle />}
                    resourceType={ResourceType.POSITION}
                    metaData={{ positionType: PositionType.REPORT }}
                    additionalFilters={accountFilter}
                    excludeColumns={EXCLUDE_POSITION_COLUMNS}
                    onReset={resetSettings}
                    leftContent={<AccountNavCash accountGUID={accountGUID} />}
                />
            )}
        />
    );
};
